/**
 * Self-test suite: Username share link (QR + verifier)
 *
 * Web port of `kaether_for_ios/Stoat/E2EE/UsernameShareLinkSelfTest.swift`.
 *
 * Four groups, 20 cases total:
 *
 *   A. Codec (9 cases):
 *      A1. universal-link encode → decode round-trip
 *      A2. custom-scheme encode → decode round-trip
 *      A3. isShareLink detects both forms
 *      A4. isShareLink rejects sibling codecs and arbitrary text
 *      A5. universal link with wrong host is rejected (typo-squat block)
 *      A6. universal link with subdomain host is rejected (allow-list)
 *      A7. universal link without fp param is rejected
 *      A8. universal link with non-16-byte fp is rejected
 *      A9. custom-scheme link with malformed base64 is rejected
 *
 *   B. Fingerprint (3 cases):
 *      B1. fingerprint is deterministic for the same key
 *      B2. fingerprint is exactly 16 bytes
 *      B3. fingerprint differs across distinct identity keys
 *
 *   C. Verifier (5 cases):
 *      C1. verifier returns ok for matching link+claim
 *      C2. verifier reports usernameMismatch on differing handles
 *      C3. verifier reports fingerprintMismatch when claim uses different key
 *      C4. verifier reports claimSignatureInvalid for tampered signature
 *      C5. fingerprint check fires before signature check (ordering invariant)
 *
 *   D. QR view model (3 cases):
 *      D1. displayHandle equals formatted handle
 *      D2. encodedString is the universal-link form
 *      D3. fallbackString is the custom-scheme form
 */

import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";
import {
  type UsernameShareLink,
  type UsernameHandle,
  CUSTOM_SCHEME_PREFIX,
  FINGERPRINT_BYTE_COUNT,
  CURRENT_SCHEMA,
  isShareLink,
  encodeUniversalLink,
  encodeCustomScheme,
  decode,
  computeFingerprint,
  base32Encode,
  formatHandle,
} from "./UsernameShareLink";
import {
  verify,
  type FetchedUsernameClaim,
} from "./UsernameShareLinkVerifier";
import { buildViewModel } from "./UsernameQRRenderingPolicy";

// ---------------------------------------------------------------------------
// Fixture helpers
// ---------------------------------------------------------------------------

function sampleHandle(canonicalName: string, discriminator: number): UsernameHandle {
  return { canonicalName, discriminator };
}

function sampleLink(
  canonicalName = "alice",
  discriminator = 1234,
  fpByte = 0xab,
  createdAt = 1_700_000_000,
): UsernameShareLink {
  return {
    schema: CURRENT_SCHEMA,
    handle: sampleHandle(canonicalName, discriminator),
    fingerprint: new Uint8Array(FINGERPRINT_BYTE_COUNT).fill(fpByte),
    createdAt,
  };
}

/**
 * Build a FetchedUsernameClaim fixture whose signature is computed by
 * the provided `sign` function. Returns both the claim and the raw key bytes.
 */
async function freshClaim(handle: UsernameHandle): Promise<{
  claim: FetchedUsernameClaim;
  publicKeyBytes: Uint8Array;
  privateKey: CryptoKey;
}> {
  const keypair = await crypto.subtle.generateKey(
    { name: "Ed25519" },
    true,
    ["sign", "verify"],
  );
  const rawPublic = await crypto.subtle.exportKey("raw", keypair.publicKey);
  const publicKeyBytes = new Uint8Array(rawPublic);

  // Canonical signed bytes: schema(8 LE) | canonicalName(UTF-8) | 0x1F |
  //   discriminator(2 LE) | 0x1F | publicKey | 0x1F | claimedAt(8 LE)
  // Mirror of iOS `UsernameClaimCodec.canonicalBytes`.
  const encoder = new TextEncoder();
  const nameBuf = encoder.encode(handle.canonicalName);
  const discriminatorBuf = new Uint8Array(2);
  new DataView(discriminatorBuf.buffer).setUint16(0, handle.discriminator, true);
  const schemaBuf = new Uint8Array(8);
  new DataView(schemaBuf.buffer).setBigUint64(0, BigInt(CURRENT_SCHEMA), true);
  const claimedAtBuf = new Uint8Array(8);
  new DataView(claimedAtBuf.buffer).setBigInt64(0, BigInt(1_700_000_000), true);

  const sep = new Uint8Array([0x1f]);
  const canonical = new Uint8Array([
    ...schemaBuf,
    ...nameBuf,
    ...sep,
    ...discriminatorBuf,
    ...sep,
    ...publicKeyBytes,
    ...sep,
    ...claimedAtBuf,
  ]);

  const signatureBuf = await crypto.subtle.sign(
    { name: "Ed25519" },
    keypair.privateKey,
    canonical,
  );
  const signatureBytes = new Uint8Array(signatureBuf);

  const claim: FetchedUsernameClaim = {
    handle,
    identityPublicKey: publicKeyBytes,
    verifySignature: async () => {
      try {
        return await crypto.subtle.verify(
          { name: "Ed25519" },
          keypair.publicKey,
          signatureBytes,
          canonical,
        );
      } catch {
        return false;
      }
    },
  };

  return { claim, publicKeyBytes, privateKey: keypair.privateKey };
}

/**
 * Return a claim with a tampered signature (one byte XOR'd).
 * The public key is unchanged so fingerprint checks still use the original key.
 */
function tamperedClaim(original: FetchedUsernameClaim): FetchedUsernameClaim {
  return {
    ...original,
    verifySignature: async () => false,
  };
}

// ---------------------------------------------------------------------------
// Group A: Codec
// ---------------------------------------------------------------------------

async function testEncodeUniversalLinkRoundTrip(): Promise<TestCase> {
  const name = "universal-link encode → decode round-trip";
  const link = sampleLink("alice", 1234, 0x11, 1_700_000_000);
  try {
    const encoded = encodeUniversalLink(link);
    const decoded = decode(encoded);
    const ok =
      decoded.handle.canonicalName === link.handle.canonicalName &&
      decoded.handle.discriminator === link.handle.discriminator &&
      decoded.fingerprint.length === link.fingerprint.length &&
      decoded.fingerprint.every((b, i) => b === link.fingerprint[i]) &&
      decoded.schema === link.schema;
    return ok
      ? pass(name)
      : fail(name, `decoded=${JSON.stringify({ handle: decoded.handle, fpLen: decoded.fingerprint.length })}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testEncodeCustomSchemeRoundTrip(): Promise<TestCase> {
  const name = "custom-scheme encode → decode round-trip";
  const link = sampleLink("bob", 42, 0x22, 1_700_000_001);
  try {
    const encoded = encodeCustomScheme(link);
    const decoded = decode(encoded);
    const ok =
      decoded.handle.canonicalName === link.handle.canonicalName &&
      decoded.handle.discriminator === link.handle.discriminator &&
      decoded.fingerprint.every((b, i) => b === link.fingerprint[i]) &&
      decoded.createdAt === link.createdAt &&
      decoded.schema === link.schema;
    return ok
      ? pass(name)
      : fail(name, `decoded=${JSON.stringify({ handle: decoded.handle, createdAt: decoded.createdAt })}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testIsShareLinkDetectsBothForms(): Promise<TestCase> {
  const name = "isShareLink detects universal and custom-scheme forms";
  const link = sampleLink();
  const universal = encodeUniversalLink(link);
  const custom = encodeCustomScheme(link);
  const ok = isShareLink(universal) && isShareLink(custom);
  return ok
    ? pass(name)
    : fail(name, `universal=${isShareLink(universal)} custom=${isShareLink(custom)}`);
}

async function testIsShareLinkRejectsSiblingCodecs(): Promise<TestCase> {
  const name = "isShareLink rejects sibling codecs and arbitrary text";
  const cases: string[] = [
    "hello world",
    "kaether:e2ee:v1:foo",
    "kaether:receipt:v1:abc",
    "kaether:tombstone:v1:abc",
    "kaether:reply:v1:abc",
    "kaether:pin:v1:abc",
    "kaether:draft:v1:abc",
    "kaether:skdm:v1:abc",
    "kaether:skmsg:v1:abc",
    "kaether:role:v1:abc",
    "kaether:gmember:v1:abc",
    "kaether:voice:v1:abc",
    "kaether:caption:v1:abc",
    "kaether:sticker:pack:v1:abc",
    "kaether:call:link:v1:abc",
    "kaether:voip-call:v1:abc",
    "kaether:voipush:v1:abc",
    "https://example.com/u/foo?fp=x",
  ];
  const failing = cases.filter((s) => isShareLink(s));
  const ok = failing.length === 0;
  return ok
    ? pass(name)
    : fail(name, `unexpectedly matched: ${failing.join(", ")}`);
}

async function testUniversalLinkWithWrongHostRejected(): Promise<TestCase> {
  const name = "universal link with wrong host is rejected";
  // kaether.example.com is a different domain — typo-squat
  const fp = base32Encode(new Uint8Array(16).fill(0xaa));
  const bad = `https://kaether.example.com/u/alice.1234?fp=${fp}`;
  try {
    decode(bad);
    return fail(name, "decode did not throw");
  } catch (e) {
    if (e instanceof Error && e.message.includes("unrecognisedHost")) {
      return pass(name);
    }
    return fail(name, `wrong error: ${String(e)}`);
  }
}

async function testUniversalLinkWithSubdomainRejected(): Promise<TestCase> {
  const name = "universal link with subdomain host is rejected";
  // sub.kaether.fr — allow-list is exact match only
  const fp = base32Encode(new Uint8Array(16).fill(0xbb));
  const bad = `https://sub.kaether.fr/u/alice.1234?fp=${fp}`;
  try {
    decode(bad);
    return fail(name, "decode did not throw");
  } catch (e) {
    if (e instanceof Error && e.message.includes("unrecognisedHost")) {
      return pass(name);
    }
    return fail(name, `wrong error: ${String(e)}`);
  }
}

async function testUniversalLinkWithoutFpRejected(): Promise<TestCase> {
  const name = "universal link without fp param is rejected";
  const bad = "https://kaether.fr/u/alice.1234";
  try {
    decode(bad);
    return fail(name, "decode did not throw");
  } catch (e) {
    if (e instanceof Error && e.message.includes("missingFingerprintParam")) {
      return pass(name);
    }
    return fail(name, `wrong error: ${String(e)}`);
  }
}

async function testUniversalLinkNon16ByteFpRejected(): Promise<TestCase> {
  const name = "universal link with non-16-byte fp is rejected";
  // Valid base32 but decodes to 10 bytes
  const shortFp = base32Encode(new Uint8Array(10).fill(0xcc));
  const bad = `https://kaether.fr/u/alice.1234?fp=${shortFp}`;
  try {
    decode(bad);
    return fail(name, "decode did not throw");
  } catch (e) {
    if (
      e instanceof Error &&
      e.message.includes("invalidFingerprintLength")
    ) {
      return pass(name);
    }
    return fail(name, `wrong error: ${String(e)}`);
  }
}

async function testCustomSchemeMalformedBase64Rejected(): Promise<TestCase> {
  const name = "custom-scheme link with malformed base64 is rejected";
  const bad = CUSTOM_SCHEME_PREFIX + "@@@not-base64@@@";
  try {
    decode(bad);
    return fail(name, "decode did not throw");
  } catch (e) {
    if (e instanceof Error && e.message.includes("malformed")) {
      return pass(name);
    }
    return fail(name, `wrong error: ${String(e)}`);
  }
}

// ---------------------------------------------------------------------------
// Group B: Fingerprint
// ---------------------------------------------------------------------------

async function testFingerprintDeterministic(): Promise<TestCase> {
  const name = "fingerprint is deterministic for the same key";
  const key = new Uint8Array(32).fill(0x77);
  const a = await computeFingerprint(key);
  const b = await computeFingerprint(key);
  const ok = a.length === b.length && a.every((v, i) => v === b[i]);
  return ok
    ? pass(name)
    : fail(name, `a=${Array.from(a).map((x) => x.toString(16)).join("")}`);
}

async function testFingerprintExactLength(): Promise<TestCase> {
  const name = "fingerprint is exactly 16 bytes";
  const key = new Uint8Array(32).fill(0x44);
  const fp = await computeFingerprint(key);
  const ok = fp.length === FINGERPRINT_BYTE_COUNT;
  return ok ? pass(name) : fail(name, `got ${fp.length}`);
}

async function testFingerprintDistinctAcrossKeys(): Promise<TestCase> {
  const name = "fingerprint differs across distinct identity keys";
  const a = await computeFingerprint(new Uint8Array(32).fill(0x01));
  const b = await computeFingerprint(new Uint8Array(32).fill(0x02));
  const ok = !a.every((v, i) => v === b[i]);
  return ok
    ? pass(name)
    : fail(name, `collision: a==b=${Array.from(a).map((x) => x.toString(16)).join("")}`);
}

// ---------------------------------------------------------------------------
// Group C: Verifier
// ---------------------------------------------------------------------------

async function testVerifierOk(): Promise<TestCase> {
  const name = "verifier returns ok for matching link+claim";
  try {
    const handle = sampleHandle("alice", 1234);
    const { claim, publicKeyBytes } = await freshClaim(handle);
    const fp = await computeFingerprint(publicKeyBytes);
    const link: UsernameShareLink = {
      schema: CURRENT_SCHEMA,
      handle,
      fingerprint: fp,
      createdAt: 1,
    };
    const result = await verify(link, claim);
    const ok = result.tag === "ok";
    return ok ? pass(name) : fail(name, `got tag=${result.tag}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testVerifierUsernameMismatch(): Promise<TestCase> {
  const name = "verifier reports usernameMismatch on differing handles";
  try {
    const linkHandle = sampleHandle("alice", 1234);
    const claimHandle = sampleHandle("bob", 5678);
    const { claim, publicKeyBytes } = await freshClaim(claimHandle);
    const fp = await computeFingerprint(publicKeyBytes);
    const link: UsernameShareLink = {
      schema: CURRENT_SCHEMA,
      handle: linkHandle,
      fingerprint: fp,
      createdAt: 1,
    };
    const result = await verify(link, claim);
    const ok =
      result.tag === "usernameMismatch" &&
      result.linkHandle.canonicalName === "alice" &&
      result.claimHandle.canonicalName === "bob";
    return ok
      ? pass(name)
      : fail(name, `got tag=${result.tag}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testVerifierFingerprintMismatch(): Promise<TestCase> {
  const name =
    "verifier reports fingerprintMismatch when claim uses different key";
  try {
    // claim signed by keyA; link fingerprint derived from keyB
    const handle = sampleHandle("alice", 1234);
    const { claim: claimA } = await freshClaim(handle);
    // Generate a different public key to derive a mismatching fingerprint
    const keyBPair = await crypto.subtle.generateKey(
      { name: "Ed25519" },
      true,
      ["sign", "verify"],
    );
    const keyBRaw = await crypto.subtle.exportKey("raw", keyBPair.publicKey);
    const keyBBytes = new Uint8Array(keyBRaw);
    const fpB = await computeFingerprint(keyBBytes);

    const link: UsernameShareLink = {
      schema: CURRENT_SCHEMA,
      handle,
      fingerprint: fpB, // fingerprint of a DIFFERENT key
      createdAt: 1,
    };
    const result = await verify(link, claimA);
    const ok = result.tag === "fingerprintMismatch";
    return ok ? pass(name) : fail(name, `got tag=${result.tag}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testVerifierClaimSignatureInvalid(): Promise<TestCase> {
  const name = "verifier reports claimSignatureInvalid for tampered claim";
  try {
    const handle = sampleHandle("alice", 1234);
    const { claim, publicKeyBytes } = await freshClaim(handle);
    const fp = await computeFingerprint(publicKeyBytes);
    const tampered = tamperedClaim(claim);
    const link: UsernameShareLink = {
      schema: CURRENT_SCHEMA,
      handle,
      fingerprint: fp, // correct fingerprint — so fingerprint check passes
      createdAt: 1,
    };
    const result = await verify(link, tampered);
    const ok = result.tag === "claimSignatureInvalid";
    return ok ? pass(name) : fail(name, `got tag=${result.tag}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testFingerprintCheckedBeforeSignature(): Promise<TestCase> {
  const name = "verifier fails fingerprint check before signature check";
  // Inject: bad fingerprint + tampered signature.
  // Verifier must return fingerprintMismatch (not claimSignatureInvalid).
  // If it returned claimSignatureInvalid we'd be doing more work than
  // necessary on attacker-supplied input.
  try {
    const handle = sampleHandle("alice", 1234);
    const { claim: claimA, publicKeyBytes: pubA } = await freshClaim(handle);
    // tamper the signature so sig-verify would fail
    const tampered = tamperedClaim(claimA);

    // Use fingerprint of a completely unrelated key — different from SHA256(pubA)
    const keyBPair = await crypto.subtle.generateKey(
      { name: "Ed25519" },
      true,
      ["sign", "verify"],
    );
    const keyBRaw = await crypto.subtle.exportKey("raw", keyBPair.publicKey);
    const keyBBytes = new Uint8Array(keyBRaw);
    const fpB = await computeFingerprint(keyBBytes);

    // Sanity: fpB != computeFingerprint(pubA)
    const fpA = await computeFingerprint(pubA);
    const same = fpA.every((v, i) => v === fpB[i]);
    if (same) {
      return fail(name, "test fixture collision: fpA === fpB (RNG failure)");
    }

    const link: UsernameShareLink = {
      schema: CURRENT_SCHEMA,
      handle,
      fingerprint: fpB, // mismatching fingerprint
      createdAt: 1,
    };
    const result = await verify(link, tampered);
    // Must be fingerprintMismatch — NOT claimSignatureInvalid
    const ok = result.tag === "fingerprintMismatch";
    return ok
      ? pass(name)
      : fail(name, `got tag=${result.tag}; expected fingerprintMismatch (ordering invariant)`);
  } catch (e) {
    return fail(name, String(e));
  }
}

// ---------------------------------------------------------------------------
// Group D: QR view model
// ---------------------------------------------------------------------------

async function testQRViewModelDisplayHandle(): Promise<TestCase> {
  const name = "QR view model displayHandle equals formatted handle";
  const link = sampleLink("alice", 1234);
  const vm = buildViewModel(link);
  const expected = formatHandle(link.handle);
  const ok = vm.displayHandle === expected;
  return ok
    ? pass(name)
    : fail(name, `got '${vm.displayHandle}' want '${expected}'`);
}

async function testQRViewModelEncodedIsUniversal(): Promise<TestCase> {
  const name = "QR view model encodedString is the universal-link form";
  const link = sampleLink("alice", 1234);
  const vm = buildViewModel(link);
  const expected = encodeUniversalLink(link);
  const ok = vm.encodedString === expected;
  return ok
    ? pass(name)
    : fail(name, `got '${vm.encodedString}' want '${expected}'`);
}

async function testQRViewModelFallbackIsCustomScheme(): Promise<TestCase> {
  const name = "QR view model fallbackString is the custom-scheme form";
  const link = sampleLink("alice", 1234);
  const vm = buildViewModel(link);
  const expected = encodeCustomScheme(link);
  const ok = vm.fallbackString === expected;
  return ok
    ? pass(name)
    : fail(name, `got '${vm.fallbackString}' want '${expected}'`);
}

// ---------------------------------------------------------------------------
// Registration
// ---------------------------------------------------------------------------

SelfTestRunner.register({
  name: "Username share link (QR + verifier)",
  isVerified: true,
  run: async () =>
    buildSuiteResult(
      await Promise.all([
        // A. Codec
        testEncodeUniversalLinkRoundTrip(),
        testEncodeCustomSchemeRoundTrip(),
        testIsShareLinkDetectsBothForms(),
        testIsShareLinkRejectsSiblingCodecs(),
        testUniversalLinkWithWrongHostRejected(),
        testUniversalLinkWithSubdomainRejected(),
        testUniversalLinkWithoutFpRejected(),
        testUniversalLinkNon16ByteFpRejected(),
        testCustomSchemeMalformedBase64Rejected(),
        // B. Fingerprint
        testFingerprintDeterministic(),
        testFingerprintExactLength(),
        testFingerprintDistinctAcrossKeys(),
        // C. Verifier
        testVerifierOk(),
        testVerifierUsernameMismatch(),
        testVerifierFingerprintMismatch(),
        testVerifierClaimSignatureInvalid(),
        testFingerprintCheckedBeforeSignature(),
        // D. QR view model
        testQRViewModelDisplayHandle(),
        testQRViewModelEncodedIsUniversal(),
        testQRViewModelFallbackIsCustomScheme(),
      ]),
    ),
});
